import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { Response } from 'node-fetch';
import { formatAdminTasks, patientProgressNoteByEntityType } from '../utils';
import Request from './Request';

/**
 * weeksummary aggregates every task administration on the chart and can run well
 * past the 8s default, which left the printing service rendering an error page
 * instead of the Completed Tasks Log. Stopgap until the query is tuned.
 */
const WEEK_SUMMARY_TIMEOUT_MS = 30000;

const allMeasuresByShortName = measures => measures.reduce((seed, val) => {
  if (val.measuretypeshortname && seed[val.measuretypeshortname]) {
    const shortName = val.measuretypeshortname;
    seed[shortName].push(val);
  } else {
    const shortName = val.measuretypeshortname;
    seed[shortName] = [val];
  }

  return seed;
}, {});

const allMeasuresByTypesandShortName = measures => measures.reduce((seed, val) => {
  const categoryName = val.measurecategoryshortname;
  const typeName = val.measuretypeshortname;

  if (categoryName && seed[categoryName]) {
    const measuresCategory = seed[categoryName];

    if (measuresCategory && measuresCategory[typeName]) {
      measuresCategory[typeName].push(val);
    } else {
      measuresCategory[typeName] = [val];
    }
  } else {
    const measuresCategory = {};
    measuresCategory[typeName] = [val];
    seed[categoryName] = measuresCategory;
  }

  return seed;
}, {});

const allMeasuresByType = measures => measures.reduce((seed, val) => {
  if (val.measurecategoryshortname && seed[val.measurecategoryshortname]) {
    const type = val.measurecategoryshortname;
    seed[type].push(val);
  } else {
    const type = val.measurecategoryshortname;
    seed[type] = [val];
  }

  return seed;
}, {});

const measuresByShortName = measures => measures.reduce((seed, val) => {
  if (val.measuretypeshortname) {
    const shortName = val.measuretypeshortname;
    seed[shortName] = val;
  }

  return seed;
}, {});
/**
 * Bucket a note under each entitykey it is tagged to, keyed for widget lookup.
 *
 * The note payload carries every non-deleted patientprogressnoteentity row, not
 * just the rows matching the entitytype that was queried. A single note can hold
 * several rows with the same entitykey under different entitytypes — a verbal
 * visit-frequency approval is tagged both CPF (order) and PCPF (care plan
 * frequency notes) with identical keys. Bucketing without an entitytype filter
 * pushes that note into the same key once per row, so it renders twice in one
 * section. Restricting to the queried entitytype keeps every distinct key,
 * including one key per discipline for a multi-discipline order.
 */


const getNoteDTO = (note, initSeed, entitytype) => {
  const entityDTO = note.patientprogressnoteentitydto || null;
  const matchingEntityDTO = entityDTO && entitytype ? entityDTO.filter(entitydto => entitydto.entitytypeshortname === entitytype) : entityDTO;
  const noteEntityDTO = matchingEntityDTO && matchingEntityDTO.length ? matchingEntityDTO.reduce((seed, entitydto) => {
    const entitykey = entitydto.entitykey || '';

    if (seed[entitykey]) {
      seed[entitykey].push(note);
    } else {
      seed[entitykey] = [note];
    }

    return seed;
  }, initSeed) : null;
  return noteEntityDTO;
}; // TODO Observation attached agency on the second mounting, can't pass observation={observation}


export class Observation extends Request {
  constructor({
    agency: _agency,
    url,
    keycloak,
    signal
  }) {
    super({
      url,
      signal,
      keycloak
    });

    _defineProperty(this, "keycloak", void 0);

    _defineProperty(this, "commonHeaders", {
      Authorization: '',
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    _defineProperty(this, "getManifestItems", async (agency, siteid, patientnumber, qtype, status, createfromdate, createtodate, isidg, formname) => {
      const response = await this.get({
        api: `/observation/patientmanifests`,
        qs: {
          patientnumber,
          agency,
          formname,
          qtype,
          status,
          createfromdate,
          createtodate,
          isidg,
          siteid
        }
      });
      return response;
    });

    _defineProperty(this, "saveManifest", async submissionForm => {
      const response = await this.post({
        api: `/observation/patientmanifest/v2`,
        data: submissionForm,
        method: 'POST'
      });
      return response;
    });

    _defineProperty(this, "updatePatientManifestV2", async (manifest, agency) => {
      const response = await this.post({
        api: `/observation/patientmanifest/v2`,
        data: JSON.stringify(manifest),
        method: 'PUT',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getIDGDataById", async (id, agency) => {
      const response = await this.get({
        api: `observation/patientmanifest/id/v2/${id}`,
        qs: {
          agency,
          formtechtype: 'IDGDATA'
        }
      });
      return response;
    });

    _defineProperty(this, "getInProgressIDG", async (agency, part, idgID, fromDate, toDate, patientNumber) => {
      const response = await this.get({
        api: `observation/patientmanifestidg`,
        qs: {
          agency,
          idg_id: idgID,
          fromdate: fromDate,
          todate: toDate,
          patientnumber: patientNumber
        }
      });
      return response;
    });

    _defineProperty(this, "savePatientManifestIDG", async (patientNumber, createPersonId, idgId, agency, siteId, fromDate, toDate) => {
      const response = await this.post({
        api: `observation/patientmanifestidg`,
        method: 'PUT',
        data: {},
        qs: {
          patientnumber: patientNumber,
          person_id: createPersonId,
          site_id: siteId,
          fromdate: fromDate,
          todate: toDate,
          idg_id: idgId,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getAgencyIdgManifests", async (siteid, createfromdate, agency, createtodate, status) => {
      const response = await this.get({
        api: `/observation/agencyidgmanifests`,
        qs: {
          siteid,
          createfromdate,
          agency,
          createtodate,
          status
        }
      });
      return response;
    });

    _defineProperty(this, "saveFajitaForm", async (submissionForm, agency) => {
      const response = await this.post({
        api: `/observation/patientmanifest/v2`,
        data: submissionForm,
        method: 'POST',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "deleteManifestForm", async (formId, agency) => {
      const response = await this.delete({
        api: `/observation/patientmanifest/id/${formId}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getSubmissionForms", async (patientNumber, createfromdate, createtodate, formname, modfromdate, modtodate, qtype, personid, agency) => {
      const response = await this.get({
        api: `/observation/formsubmissions`,
        qs: {
          patientNumber,
          createfromdate,
          createtodate,
          formname,
          modfromdate,
          modtodate,
          qtype,
          personid,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getMeasureTypes", async (category, patientnumber, agency) => {
      const response = await this.get({
        api: `/observation/measuretypes`,
        qs: {
          agency,
          category,
          patientnumber
        }
      });
      return response;
    });

    _defineProperty(this, "getAllMeasureTypes", async (patientnumber, agency) => {
      const response = await this.get({
        api: `/observation/measuretypes`,
        qs: {
          agency,
          patientnumber
        }
      });

      if (Array.isArray(response)) {
        return allMeasuresByType(response);
      }

      return response;
    });

    _defineProperty(this, "getPatientMeasures", async (patientNumber, agency, category, fromdate, todate, type) => {
      const response = await this.get({
        api: `/observation/patientmeasures/patient/${patientNumber}`,
        qs: {
          fromdate,
          todate,
          category,
          type,
          agency
        }
      });

      if (Array.isArray(response)) {
        return allMeasuresByShortName(response);
      }

      return response;
    });

    _defineProperty(this, "getPrintPatientMeasures", async (patientNumber, agency, fromdate, todate, category, type) => {
      const response = await this.get({
        api: `/observation/patientmeasures/patient/${patientNumber}`,
        qs: {
          fromdate,
          todate,
          category,
          type,
          agency
        }
      });

      if (Array.isArray(response)) {
        return allMeasuresByTypesandShortName(response);
      }

      return response;
    });

    _defineProperty(this, "getPatientMeasureLatest", async (patientNumber, type) => {
      const response = await this.get({
        api: `/observation/patientmeasurelatest/patient/${patientNumber}/${type}`
      });

      if (Array.isArray(response)) {
        return measuresByShortName(response);
      }

      return response;
    });

    _defineProperty(this, "getPatientMeasuresLatest", async patientNumber => {
      const response = await this.get({
        api: `/observation/patientmeasureslatest/patient/${patientNumber}`
      });

      if (Array.isArray(response)) {
        return measuresByShortName(response);
      }

      return response;
    });

    _defineProperty(this, "savePatientMeasure", async (patientMeasure, method) => {
      const response = await this.post({
        api: `/observation/patientmeasure`,
        data: patientMeasure,
        method
      });
      return response;
    });

    _defineProperty(this, "deletePatientMeasure", async patientMeasure => {
      const response = await this.delete({
        api: `/observation/patientmeasuredelete`,
        data: patientMeasure
      });
      return response;
    });

    _defineProperty(this, "getProgressNotes", async (patientNumber, agency, showhiddenordersandnotes) => {
      const response = await this.get({
        api: `/observation/patientprogressnotes/patient/${patientNumber}`,
        qs: {
          agency,
          showhiddenordersandnotes
        }
      });
      return Array.isArray(response) ? patientProgressNoteByEntityType(response) : response;
    });

    _defineProperty(this, "getProgressNotesById", async (entityId, agency) => {
      const response = await this.get({
        api: `/observation/patientprogressnote/id/${entityId}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getProgressNotesByEntity", async (patientNumber, agency, entityType, entityKey) => {
      const response = await this.get({
        api: `/observation/patientprogressnotes/patiententitytype`,
        qs: {
          patientnumber: patientNumber,
          agency,
          entitytype: entityType,
          entitykey: entityKey
        }
      });
      return response;
    });

    _defineProperty(this, "getProgressNotesByEntityDTO", async (patientNumber, agency, entityType, entityKey) => {
      const response = await this.get({
        api: `/observation/patientprogressnotes/patiententitytype`,
        qs: {
          patientnumber: patientNumber,
          agency,
          entitytype: entityType,
          entitykey: entityKey
        }
      });
      return Array.isArray(response) ? patientProgressNoteByEntityType(response) : response;
    });

    _defineProperty(this, "getProgressNoteCountsByEntity", async (agency, entityType, patientNumber, entityKey) => {
      const response = await this.get({
        api: `/observation/patientprogressnotes/notecounts`,
        qs: {
          agency,
          entitytype: entityType,
          patientnumber: patientNumber,
          entitykey: entityKey
        }
      });
      return response;
    });

    _defineProperty(this, "getPrintProgressNotes", async (patientnumber, agency, fromdate, todate, meeting_id) => {
      const response = await this.get({
        api: `/observation/patientprogressnotes/patientdaterange`,
        qs: {
          patientnumber,
          agency,
          fromdate,
          todate,
          meeting_id
        }
      });
      return response;
    });

    _defineProperty(this, "getPnNotes", async (data, agency, patientNumber) => {
      const notes = await Promise.all(data.map(value => this.getProgressNotesByEntity(patientNumber, agency, value.entitytype, value.entitykey)));
      const ids = data.map(value => value.entitykey);
      const pnNotes = notes && Object.fromEntries(ids.map((_, i) => [ids[i], notes[i]]));
      return pnNotes;
    });

    _defineProperty(this, "getWidgetPnNotes", async (patientNumber, agency, entitytype) => {
      const notes = await this.getProgressNotesByEntity(patientNumber, agency, entitytype);
      const pnNotes = !(notes instanceof Response) && notes.length ? notes.reduce((seed, plan) => {
        const noteDTO = getNoteDTO(plan, seed, entitytype);
        let newSeed = seed;

        if (noteDTO) {
          newSeed = _objectSpread(_objectSpread({}, noteDTO), seed);
        }

        return newSeed;
      }, {}) : {};
      return pnNotes;
    });

    _defineProperty(this, "saveProgressNote", async (progressNote, agency, method, ignoreentitydto) => {
      const response = await this.post({
        api: `/observation/patientprogressnote`,
        data: JSON.stringify(progressNote),
        method: method || 'POST',
        qs: {
          agency,
          ignoreentitydto
        }
      });
      return response;
    });

    _defineProperty(this, "saveProgressNoteEntity", async (progressNoteEntity, agency, method) => {
      let response;

      if (method === 'DELETE') {
        response = await this.delete({
          api: `/observation/patientprogressnoteentity`,
          data: JSON.stringify(progressNoteEntity),
          method: 'DELETE',
          qs: {
            agency
          }
        });
      } else {
        response = await this.post({
          api: `/observation/patientprogressnoteentity`,
          data: JSON.stringify(progressNoteEntity),
          method: method || 'POST',
          qs: {
            agency
          }
        });
      }

      return response;
    });

    _defineProperty(this, "getStandingOrders", async (patientnumber, agency, soshortname) => {
      const response = await this.get({
        api: `/observation/standingorders/v3`,
        qs: {
          patientnumber,
          agency,
          soshortname
        }
      });
      return response;
    });

    _defineProperty(this, "addPatientStandingOrder", async (patientStandingOrder, agency) => {
      const response = await this.post({
        api: `observation/patientstandingorder/v2`,
        data: JSON.stringify(patientStandingOrder),
        method: 'POST',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientStandingOrderById", async (id, agency) => {
      const response = await this.get({
        api: `/observation/patientstandingorder/id/${id}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientStandingOrderByIdV2", async (id, agency) => {
      const response = await this.get({
        api: `/observation/patientstandingorder/v2/id/${id}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientStandingOrders", async (patientnumber, latest, agency, ineffectfromdate, ineffecttodate) => {
      const response = await this.get({
        api: `/observation/patientstandingorders`,
        qs: {
          patientnumber,
          latest,
          agency,
          ineffectfromdate,
          ineffecttodate
        }
      });
      return response;
    });

    _defineProperty(this, "approveProgressNote", async (progressNote, agency) => {
      const response = await this.post({
        api: `/observation/patientprogressnote`,
        data: JSON.stringify(progressNote),
        method: 'PUT',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "deleteProgressNote", async (id, agency) => {
      const response = await this.delete({
        api: `/observation/patientprogressnote/id/${id}`,
        method: 'DELETE',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientCarePlanProblemsv2", async (patientnumber, latest, submode, agency) => {
      const response = await this.get({
        api: `/observation/patientcareplanproblems/v2`,
        qs: {
          patientnumber,
          latest,
          submode,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientCarePlanProblemsByDate", async (patientnumber, latest, submode, ineffectfromdate, ineffecttodate, agency) => {
      // print returns all the cpp that are submitted INCLUDING resolved plans
      const response = await this.get({
        api: `/observation/patientcareplanproblems/v2`,
        qs: {
          patientnumber,
          latest,
          submode,
          ineffectfromdate,
          ineffecttodate,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getCarePlanProblemsv2", async (patientnumber, agency) => {
      const response = await this.get({
        api: `/observation/careplanproblems/v2`,
        qs: {
          patientnumber,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientCarePlanProblemByIdv2", async (id, submode, agency) => {
      const response = await this.get({
        api: `/observation/patientcareplanproblem/v2/id/${id}`,
        qs: {
          submode,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getCarePlanProblemById", async (id, agency) => {
      const response = await this.get({
        api: `/observation/careplanproblem/v2/id/${id}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "savePatientCarePlanProblemv2", async (patientCarePlan, agency, method) => {
      const response = await this.post({
        api: `/observation/patientcareplanproblem/v2`,
        data: JSON.stringify(patientCarePlan),
        method: method || 'POST',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "resolveCarePlanProblem", async (patientCarePlan, agency, method) => {
      const response = await this.post({
        api: `/observation/patientcareplanproblemresolve/v2`,
        data: JSON.stringify(patientCarePlan),
        method: method || 'POST',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "deleteCarePlanProblem", async (id, agency) => {
      const response = await this.delete({
        api: `/observation/patientcareplanproblem/id/${id}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientCppTasksById", async (patientcppid, agency) => {
      const response = await this.get({
        api: `/observation/patientcpptasks/patientcpp/${patientcppid}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getCppTasksById", async (cppid, agency, tasktext) => {
      const response = await this.get({
        api: `/observation/cpptasks/cppid/${cppid}`,
        qs: {
          agency,
          tasktext
        }
      });
      return response;
    });

    _defineProperty(this, "savePatientCppTasks", async (cppTasks, agency) => {
      const response = await this.post({
        api: `/observation/patientcpptasks`,
        data: JSON.stringify(cppTasks),
        method: 'PUT',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getAdminTasks", async (agency, siteid, includedc, patientnumber, fromdate, todate, visitdate) => {
      const response = await this.get({
        api: `/observation/patientcpptasks/active/v2`,
        qs: {
          agency,
          siteid,
          includedc,
          patientnumber,
          fromdate,
          todate,
          visitdate
        }
      });

      if (!(response instanceof Response)) {
        return formatAdminTasks(response);
      }

      return response;
    });

    _defineProperty(this, "getCurrentWeekAdminTasks", async (agency, siteid, includedc, patientnumber, visitdate) => {
      const limitbyweeksback = '0';
      const response = await this.get({
        api: `/observation/patientcpptasks/active/v2`,
        qs: {
          agency,
          siteid,
          includedc,
          patientnumber,
          limitbyweeksback,
          visitdate
        }
      });

      if (!(response instanceof Response)) {
        return formatAdminTasks(response);
      }

      return response;
    });

    _defineProperty(this, "saveAdminTasks", async (adminTasks, agency) => {
      const response = await this.post({
        api: `/observation/patientcpptasks/administrations`,
        data: JSON.stringify(adminTasks),
        method: 'PUT',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "deleteAdminTask", async (id, agency) => {
      const response = await this.delete({
        api: `/observation/patientcpptasks/administration/id/${id}`,
        method: 'DELETE',
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientAdminsbyCPPTask", async (pcpptaskid, agency) => {
      const response = await this.get({
        api: `/observation/patientcpptasks/administrations/patientcpptask/${pcpptaskid}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientWeeklyTasks", async (agency, siteid, patientnumber, adminfromdate, admintodate, includedetails, view) => {
      const response = await this.get({
        api: `/observation/patientcpptasks/weeksummary`,
        qs: {
          patientnumber,
          agency,
          siteid,
          adminfromdate,
          admintodate,
          includedetails,
          view
        },
        timeoutMs: WEEK_SUMMARY_TIMEOUT_MS
      });
      return response;
    });

    _defineProperty(this, "getPatientTasksDetails", async (pcpptaskkey, agency) => {
      const response = await this.get({
        api: `/observation/patientcpptasks/administrations/patientcpptaskkey/${pcpptaskkey}`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getFajitaFormByIdByMode", async (id, submode, agency) => {
      const response = await this.get({
        api: `observation/patientmanifest/id/v2/${id}`,
        qs: {
          agency,
          submode
        }
      });
      return response;
    });

    _defineProperty(this, "getFajitaFormWithExtraData", async (id, submode, agency, siteid) => {
      const response = await this.get({
        api: `observation/patientmanifest/id/v3/${id}`,
        qs: {
          agency,
          submode,
          siteid
        }
      });
      return response;
    });

    _defineProperty(this, "getFajitaFormById", async (id, patientnumber, agency) => {
      const response = await this.get({
        api: `observation/form/id/${id}`,
        qs: {
          agency,
          patientnumber
        }
      });
      return response;
    });

    _defineProperty(this, "getFormStickiesById", async (id, patientnumber, agency) => {
      const response = await this.get({
        api: `observation/formsticky/id/${id}`,
        qs: {
          agency,
          patientnumber
        }
      });
      return response;
    });

    _defineProperty(this, "getIncidentTypes", async agency => {
      const response = await this.get({
        api: `observation/incident/getIncidentTypes`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getIncidentsForSite", async (agency, siteid, fromdate, todate) => {
      const response = await this.get({
        api: `observation/incident/getall`,
        qs: {
          agency,
          siteid,
          fromdate,
          todate
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientIncidents", async (patientNumber, agency, siteid, fromdate, todate) => {
      const response = await this.get({
        api: `observation/incident/getall/${patientNumber}`,
        qs: {
          agency,
          siteid,
          fromdate,
          todate
        }
      });
      return response;
    });

    _defineProperty(this, "postIncident", async (incident, agency, siteid) => {
      const response = await this.post({
        api: `observation/incident/add`,
        qs: {
          agency,
          siteid
        },
        data: incident
      });
      return response;
    });

    _defineProperty(this, "syncPatientVisits", async (patientvisit_id, visitdatetime, agency) => {
      const response = await this.post({
        api: `observation/syncvisit/patientvisit_id/${patientvisit_id}`,
        qs: {
          agency,
          visitdatetime
        }
      });
      return response;
    });

    _defineProperty(this, "getObservationVisits", async (id, agency) => {
      const response = await this.get({
        api: `/patientvisitobservation/patientvisitid/${id}`,
        qs: {
          agency
        }
      });
      return response;
    });

    this.url = new URL(url);
    this.keycloak = keycloak;

    if (_agency) {
      this.baseParams = {
        agency: _agency
      };
    }

    Object.defineProperty(this.commonHeaders, 'Authorization', {
      get() {
        const {
          token
        } = keycloak;
        return `Bearer ${token}`;
      },

      set(newValue) {// need a setter
      }

    });
    this.commonHeaders.Authorization = `Bearer ${keycloak.token}`;
  }

}
export default Observation;